// Mirrored from mju-ai-workbench/shared/schemas/nodeRemote.ts (v0.85.0 e578dc9a) — keep the two identical but for this line and the import extensions.
/**
 * Remote control of a node from the organisation's console and Mojo Up AI Cloud, the phone included (roadmap D290,
 * docs/plans/2026-10-node-remote-control.md). Workbench-owned until AIOE mirrors it, as `runnerOrders.ts` was for D277:
 * the mirrored `deviceOrders.ts` is untouched, and `runnerOrders.ts` folds these kinds into what the workbench reads.
 *
 * Two mechanisms, one rule each:
 *
 *  - **Configuration goes as a device order** (D257's heartbeat path): applied once per id, answered on the next
 *    heartbeat, kept in the console's history. The D257 / D277 kinds cover models, runtimes, DLP, lockable settings
 *    and the runner; {@link NODE_ORDER_KINDS} add what a node's CLI changes and no order did: start at login, the
 *    update channel and automatic updates, a routine run or removed, remote control of the node person's sessions,
 *    and the node's own `node.*` settings. A desktop refuses every one of them in words (`nodes_only`).
 *  - **Immediate, interactive things go over the relay's rpc**: `node.subscribe` (a long poll, the shape of D282's
 *    `sessions.subscribe`) and `node.snapshot` to watch, and the control calls ({@link NODE_CONTROL_METHODS}) to answer
 *    an approval, stop a member, restart the service, check or install an update. Every control call carries
 *    `via: "phone" | "web"` inside the signed rpc.
 *
 * Words for people: node, repository, member, approval; never an id.
 *
 * Pure: zod and plain functions only.
 */
import { z } from "zod";
import { REMOTE_VIA } from "./remoteControl.js";
// ── The order kinds (nodes only) ────────────────────────────────────────
export const NODE_ORDER_KINDS = ["service.configure", "update.configure", "routine.run", "routine.remove", "remote.configure", "node.setting.set"];
export const nodeOrderKindSchema = z.enum(NODE_ORDER_KINDS);
export function isNodeOrderKind(kind) {
    return typeof kind === "string" && NODE_ORDER_KINDS.includes(kind);
}
/** `service.configure`: start the node when the machine starts (`mojoup-node service install` / `uninstall`). */
export const serviceConfigureBodySchema = z.object({ startAtLogin: z.boolean() }).strict();
/** `update.configure`: the install's channel (installs the newest on it, as `update --channel`), and automatic updates (`node.autoUpdate`). */
export const updateConfigureBodySchema = z
    .object({ channel: z.enum(["stable", "nightly"]).optional(), autoUpdate: z.boolean().optional() })
    .strict()
    .refine((b) => b.channel !== undefined || b.autoUpdate !== undefined, { message: "channel or autoUpdate" });
/** `routine.run` / `routine.remove`: a routine kept on the node, by its id; `name` is words for the sentence. */
export const routineOrderBodySchema = z.object({ id: z.string().min(1).max(200), name: z.string().max(200).optional() }).strict();
/** `remote.configure`: remote control from the web and the phone for every session the node runs for its person (`mojoup-node remote on|off`). */
export const remoteConfigureBodySchema = z.object({ allSessions: z.boolean() }).strict();
/** `node.setting.set`: one of the node's own `node.*` settings (`mojoup-node config set`); `value: null` puts it back to its default. */
export const nodeSettingBodySchema = z.object({ key: z.string().min(1).max(200), value: z.union([z.string().max(1000), z.number(), z.boolean(), z.array(z.string().max(200)).max(50), z.null()]) }).strict();
const nodeOrderBase = {
    id: z.string().min(1).max(100),
    issuedAt: z.string().max(64).optional(),
    /** Who asked, in words (a person's name or email), as on every order. */
    by: z.string().max(200).optional(),
    /** Who asked, as the organisation knows them (their principal): what `requireOwnerForService` compares with the node's owner, and the audit keeps. */
    askedBy: z.string().max(200).optional(),
    expiresAt: z.string().max(64).optional(),
    batchId: z.string().max(100).optional(),
};
/** One node order on the heartbeat answer's `orders` list. */
export const nodeOrderSchema = z.discriminatedUnion("kind", [
    z.object({ ...nodeOrderBase, kind: z.literal("service.configure"), body: serviceConfigureBodySchema }),
    z.object({ ...nodeOrderBase, kind: z.literal("update.configure"), body: updateConfigureBodySchema }),
    z.object({ ...nodeOrderBase, kind: z.literal("routine.run"), body: routineOrderBodySchema }),
    z.object({ ...nodeOrderBase, kind: z.literal("routine.remove"), body: routineOrderBodySchema }),
    z.object({ ...nodeOrderBase, kind: z.literal("remote.configure"), body: remoteConfigureBodySchema }),
    z.object({ ...nodeOrderBase, kind: z.literal("node.setting.set"), body: nodeSettingBodySchema }),
]);
/** What a node order asks, as the end of "Your organisation asked this machine to …". */
export function describeNodeOrder(order) {
    switch (order.kind) {
        case "service.configure":
            return order.body.startAtLogin ? "start the node when the machine starts" : "stop starting the node when the machine starts";
        case "update.configure": {
            const parts = [];
            if (order.body.channel)
                parts.push(`move to the ${order.body.channel} channel and install its newest version`);
            if (order.body.autoUpdate !== undefined)
                parts.push(order.body.autoUpdate ? "update itself automatically" : "stop updating itself automatically");
            return parts.join(" and ");
        }
        case "routine.run":
            return `run the routine ${order.body.name ? `“${order.body.name}”` : "it named"} now`;
        case "routine.remove":
            return `remove the routine ${order.body.name ? `“${order.body.name}”` : "it named"}`;
        case "remote.configure":
            return order.body.allSessions ? "open every session it runs for its person to the web and the phone" : "close every session it runs for its person to the web and the phone";
        case "node.setting.set":
            return order.body.value === null ? `put the setting ${order.body.key} back to its default` : `set ${order.body.key} to ${JSON.stringify(order.body.value)}`;
    }
}
// ── What the organisation's policy can allow (`remote.nodeControl`) ─────
/**
 * The actions `remote.nodeControl.actions` names. Left out, every one. `service` and `updates` are the ones
 * `requireOwnerForService` keeps for the node's owner.
 */
export const NODE_CONTROL_ACTIONS = ["watch", "approvals", "members", "service", "updates", "settings", "routines", "remote-sessions"];
/** The words each action is refused with: "Your organisation's policy does not allow <words> from the console." */
export const NODE_CONTROL_ACTION_WORDS = {
    watch: "watching this node",
    approvals: "answering this node's approvals",
    members: "stopping this node's members",
    service: "restarting this node or changing how it starts",
    updates: "updating this node",
    settings: "changing this node's settings",
    routines: "running or removing this node's routines",
    "remote-sessions": "opening or closing this node's sessions to the web and the phone",
};
/** Which action an order kind is. */
export const NODE_ORDER_ACTION = {
    "service.configure": "service",
    "update.configure": "updates",
    "routine.run": "routines",
    "routine.remove": "routines",
    "remote.configure": "remote-sessions",
    "node.setting.set": "settings",
};
/** The actions only the node's owner may take when `requireOwnerForService` is on. */
export const OWNER_ONLY_ACTIONS = ["service", "updates"];
// ── Refusals ────────────────────────────────────────────────────────────
/**
 * The id beside a refusal's words: an order answer's `code`, an rpc refusal's `data.code` (JSON-RPC -32003). The words
 * stay what a person reads.
 */
export const NODE_REMOTE_REFUSALS = {
    /** `remote.nodeControl.allowed: false`: the organisation does not manage its nodes from the console. */
    node_control_off: "node_control_off",
    /** `remote.nodeControl.killSwitch: true`, or the node's own kill switch: nothing from the console now. */
    kill_switch: "kill_switch",
    /** The action is not in `remote.nodeControl.actions`. */
    action_not_allowed: "action_not_allowed",
    /** `requireOwnerForService`: a service or update action from someone other than the node's owner. */
    owner_required: "owner_required",
    /** The setting is locked by the organisation's policy (changed by publishing the policy). */
    setting_locked: "setting_locked",
    /** Not a key a node order changes, or a value the setting does not take. */
    invalid_order: "invalid_order",
    /** The value looks like a secret, or the key holds one. */
    secret_refused: "secret_refused",
    /** A desktop: the node kinds and methods are a node's only. */
    nodes_only: "nodes_only",
    /** The request names no person (a LAN-paired device, an unsigned relay envelope). */
    names_nobody: "names_nobody",
    /** Something only a person at the machine can do (a runtime's own sign-in). */
    not_possible_remotely: "not_possible_remotely",
    /** No such routine, member or approval here (any more). */
    not_found: "not_found",
};
// ── The rpc methods ─────────────────────────────────────────────────────
/**
 * The methods a node serves to the organisation over the relay (`POST /relay/v1/devices/:id/rpc`), with the
 * Control API scope each needs. A signed caller only (the person in the signature); refused from anything else.
 */
export const NODE_REMOTE_METHODS = {
    subscribe: "node.subscribe",
    snapshot: "node.snapshot",
    resolve: "node.permissions.resolve",
    stopMember: "node.sessions.stop",
    restart: "node.service.restart",
    updateCheck: "node.updates.check",
    updateInstall: "node.updates.install",
};
export const NODE_REMOTE_METHOD_SCOPES = {
    "node.subscribe": "agents:read",
    "node.snapshot": "agents:read",
    "node.permissions.resolve": "permissions:write",
    "node.sessions.stop": "agents:write",
    "node.service.restart": "settings:write",
    "node.updates.check": "agents:read",
    "node.updates.install": "settings:write",
};
/** The control calls: each takes `via` and is audited `node.remote.*`. */
export const NODE_CONTROL_METHODS = ["node.permissions.resolve", "node.sessions.stop", "node.service.restart", "node.updates.install"];
/** The audit actions, on the node's own chain. */
export const NODE_REMOTE_AUDIT = {
    watch: "node.remote.watch",
    approve: "node.remote.approve",
    deny: "node.remote.deny",
    stopMember: "node.remote.stop-member",
    restart: "node.remote.restart",
    updateCheck: "node.remote.update-check",
    updateInstall: "node.remote.update-install",
    service: "node.remote.service",
    update: "node.remote.update",
    routine: "node.remote.routine",
    sessions: "node.remote.sessions",
    setting: "node.remote.setting",
};
/** The audit action an order kind is recorded under. */
export const NODE_ORDER_AUDIT = {
    "service.configure": NODE_REMOTE_AUDIT.service,
    "update.configure": NODE_REMOTE_AUDIT.update,
    "routine.run": NODE_REMOTE_AUDIT.routine,
    "routine.remove": NODE_REMOTE_AUDIT.routine,
    "remote.configure": NODE_REMOTE_AUDIT.sessions,
    "node.setting.set": NODE_REMOTE_AUDIT.setting,
};
export const viaSchema = z.enum(REMOTE_VIA);
// ── Words ───────────────────────────────────────────────────────────────
/** "1 min ago", "just now", "3 h ago". */
export function agoWords(ms) {
    if (!Number.isFinite(ms) || ms < 45_000)
        return "just now";
    const m = Math.round(ms / 60_000);
    if (m < 60)
        return `${m} min ago`;
    const h = Math.round(m / 60);
    return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} days ago`;
}
/** The banner: "Managed from the console by Dana Lee · 2 min ago", "Managed from the phone by Dana Lee · just now". */
export function managedWords(by, via, atMs, nowMs) {
    const from = via === "phone" ? "the phone" : "the console";
    return `Managed from ${from} by ${by} · ${agoWords(nowMs - atMs)}`;
}
//# sourceMappingURL=nodeRemote.js.map